import React, { useMemo } from "react";
import {
  Box,
  Divider,
  useTheme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import ColorPicker from "../../../../../../views/components/common/color/ColorPicker";
import ImageUpload from "../../../../../../views/components/common/upload/ImageUpload";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import FormTextField from "../../../../../../views/components/common/form/FormTextField";
import { useStores } from "../../../../use-stores";
import {
  BrandingImageValue,
  BrandingModel,
} from "../../../../../../models/Swagstore/BrandingModel";
import { ImageData } from "../../../../../../models/Image/Image";
import { EmployeeRoles } from "../../../../../../models/Employees/EmployeeRoles";

interface BrandingSettingsProps {
  settings: BrandingModel;
  onSettingChange: (key: keyof BrandingModel | string, value: any) => void;
  onSaveSettings: () => void;
}

const BrandingSettings: React.FC<BrandingSettingsProps> = ({
  settings,
  onSettingChange,
  onSaveSettings,
}) => {
  const theme = useTheme();

  const { swagStoreStore, userStore } = useStores();

  type BrandingImageField =
    // | "bannerImage"
    "carouselImages" | "logo" | "favicon";

  const splitImages = (
    images?: BrandingImageValue[] | null,
    labelPrefix?: string
  ) => {
    const result = {
      files: [] as File[],
      existing: [] as ImageData[],
    };

    if (!images) {
      return result;
    }

    images.forEach((image, index) => {
      if (image instanceof File) {
        result.files.push(image);
      } else if (typeof image === "string") {
        result.existing.push({
          id: 1, // Add id so deleteImage is called instead of removeImage (any truthy value works)
          img: image,
          title: `${labelPrefix || "Image"} ${index + 1}`,
        });
      }
    });

    return result;
  };

  const removeBrandingImage = (
    field: BrandingImageField,
    matcher: (image: BrandingImageValue) => boolean
  ) => {
    const current = swagStoreStore.settings.branding?.[field];
    if (!current) return;

    const filtered = current.filter((image) => !matcher(image));
    swagStoreStore.updateBranding({
      [field]: filtered.length > 0 ? filtered : [],
    });
  };

  // const bannerImages = useMemo(
  //   () => splitImages(settings.bannerImage, "Hero Image"),
  //   [settings.bannerImage]
  // );

  const carouselImages = useMemo(
    () => splitImages(settings.carouselImages, "Carousel Image"),
    [settings.carouselImages]
  );

  const logoImages = useMemo(
    () => splitImages(settings.logo, "Logo"),
    [settings.logo]
  );

  const faviconImages = useMemo(
    () => splitImages(settings.favicon, "Favicon"),
    [settings.favicon]
  );

  return (
    <>
      <Box>
        <CustomTypography
          variant="heading6"
          fontWeight={600}
          style={{ marginBottom: "12px" }}
        >
          Branding & Banner
        </CustomTypography>
        <CustomTypography
          variant="heading9"
          color={theme.palette.grey[600]}
          style={{ marginBottom: "12px" }}
        >
          Configure Logo, Colors, Hero Banner
        </CustomTypography>

        <Divider sx={{ my: 3 }} />

        {/* <Box
          sx={{
            mb: 3,
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          <CustomTypography
            variant="body1"
            fontWeight={600}
            style={{ marginBottom: "12px" }}
          >
            Main Hero Banner
          </CustomTypography>

          <Box
            sx={{
              mb: 2,
            }}
          >
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "12px", color: theme.palette.grey[800] }}
            >
              Hero Image
            </CustomTypography>
            <ImageUpload
              id="hero-image"
              key="hero-image"
              allowedFileTypes="image/*"
              fileInfo="SVG, PNG, JPG up to 2MB"
              showImagesList={false}
              size="large"
              multiple={false}
              uploadText="Upload Hero Image"
              previewImage={true}
              minWidth="100%"
              minHeight="134px"
              existingImageList={bannerImages.existing}
              newImageList={bannerImages.files}
              deleteImage={(imageData) => {
                removeBrandingImage(
                  "bannerImage",
                  (image) =>
                    typeof image === "string" && image === imageData.img
                );
              }}
              removeImage={(imageData) => {
                removeBrandingImage(
                  "bannerImage",
                  (image) =>
                    image instanceof File && image.name === imageData.title
                );
              }}
              addImagesToBeUploaded={(files) =>
                swagStoreStore.updateBranding({
                  bannerImage: files,
                })
              }
              disabled={false}
            />
          </Box>
        </Box> */}

        {/* <Divider sx={{ my: 3 }} /> */}

        <Box
          sx={{
            mb: 3,
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              mb: 2,
            }}
          >
            <CustomTypography
              variant="body1"
              fontWeight={600}
              style={{ marginBottom: "12px" }}
            >
              Banner Images
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.grey[600]}
              style={{ marginBottom: "12px", fontSize: "0.75rem" }}
            >
              Images displayed in the carousel banner at the top of your store
              homepage to showcase featured content
            </CustomTypography>
            <ImageUpload
              id="carousel-images"
              key="carousel-images"
              allowedFileTypes="image/*"
              fileInfo="SVG, PNG, JPG up to 2MB | Recommended size: 1920x480 pixels"
              showImagesList={true}
              size="large"
              multiple={true}
              uploadText="Upload Banner Images"
              // previewImage={false}
              minWidth="100%"
              minHeight="134px"
              existingImageList={carouselImages.existing}
              newImageList={carouselImages.files}
              deleteImage={(imageData) => {
                const current =
                  swagStoreStore.settings.branding?.carouselImages;
                if (!current || !Array.isArray(current)) return;

                // Get the URL to delete - imageData.img contains the URL string
                const urlToDelete = imageData.img;
                if (!urlToDelete) return;

                // The store has carouselImages as an array of strings (URLs)
                // Filter out the matching URL
                const filtered = current.filter((image) => {
                  // Handle both string URLs and File objects
                  if (typeof image === "string") {
                    // Compare URLs exactly (no trimming needed as URLs from backend are clean)
                    return image !== urlToDelete;
                  }
                  // Keep File objects (newly uploaded files)
                  return true;
                });

                // Always create a new array reference for MobX reactivity
                const newCarouselImages =
                  filtered.length > 0 ? [...filtered] : [];

                // Update the store with the new array
                swagStoreStore.updateBranding({
                  carouselImages: newCarouselImages,
                });
              }}
              removeImage={(imageData) => {
                const current =
                  swagStoreStore.settings.branding?.carouselImages;
                if (!current) return;

                // Directly filter out the file that matches the name
                const filtered = current.filter(
                  (image) =>
                    !(image instanceof File && image.name === imageData.title)
                );

                swagStoreStore.updateBranding({
                  carouselImages: filtered.length > 0 ? filtered : [],
                });
              }}
              addImagesToBeUploaded={(files) => {
                if (!files || files.length === 0) return;

                // Get current carousel images (both existing strings and new Files)
                const currentCarouselImages =
                  swagStoreStore.settings.branding?.carouselImages || [];

                // Ensure we have an array
                const existingImages = Array.isArray(currentCarouselImages)
                  ? currentCarouselImages
                  : [];

                // Combine existing images with new files
                // Existing images are strings (URLs), new files are File objects
                const updatedCarouselImages = [
                  ...existingImages, // Preserve all existing images (strings)
                  ...files, // Add new files
                ];

                // Update the store with the combined array
                swagStoreStore.updateBranding({
                  carouselImages: updatedCarouselImages,
                });
              }}
              disabled={false}
            />
          </Box>
        </Box>

        <Box
          sx={{
            mb: 3,
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          <CustomTypography
            variant="body1"
            fontWeight={600}
            style={{ marginBottom: "12px" }}
          >
            Product Section Content
          </CustomTypography>

          <Box sx={{ mb: 1 }}>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "12px", color: theme.palette.grey[800] }}
            >
              Heading Text
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.grey[600]}
              style={{ marginBottom: "12px", fontSize: "0.75rem" }}
            >
              Main heading displayed above the product section on your store
              homepage
            </CustomTypography>
            <FormTextField
              placeholder="Enter heading text"
              value={settings.productHeadingText || ""}
              onChange={(id, value) =>
                onSettingChange("productHeadingText", value || "")
              }
              sx={{ marginBottom: "0px" }}
              backgroundColor={theme.palette.customColors.grayBackGroundColor}
              borderColor="none"
            />
          </Box>
        </Box>
        {/* <Divider sx={{ my: 3 }} /> */}

        <Box
          sx={{
            mb: 3,
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          <CustomTypography
            variant="body1"
            fontWeight={600}
            style={{ marginBottom: "12px" }}
          >
            Logo & Assets
          </CustomTypography>

          <Box
            sx={{
              mb: 2,
            }}
          >
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "12px", color: theme.palette.grey[800] }}
            >
              Logo
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.grey[600]}
              style={{ marginBottom: "12px", fontSize: "0.75rem" }}
            >
              Your store logo displayed in the header and used for branding
              throughout the store
            </CustomTypography>
            <ImageUpload
              id="logo"
              key="logo"
              minHeight="134px"
              minWidth="100%"
              allowedFileTypes="image/*"
              fileInfo="SVG, PNG, JPG up to 2MB | Recommended size: 200x200 pixels"
              showImagesList={false}
              previewImage={true}
              size="large"
              multiple={false}
              uploadText="Upload Logo"
              existingImageList={logoImages.existing}
              newImageList={logoImages.files}
              deleteImage={(imageData) => {
                removeBrandingImage(
                  "logo",
                  (image) =>
                    typeof image === "string" && image === imageData.img
                );
              }}
              removeImage={(imageData) => {
                removeBrandingImage(
                  "logo",
                  (image) =>
                    image instanceof File && image.name === imageData.title
                );
              }}
              addImagesToBeUploaded={(files) =>
                swagStoreStore.updateBranding({
                  logo: files,
                })
              }
              disabled={false}
            />
          </Box>

          <Box sx={{ mb: 0.5 }}>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "12px", color: theme.palette.grey[800] }}
            >
              Favicon
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.grey[600]}
              style={{ marginBottom: "12px", fontSize: "0.75rem" }}
            >
              Small icon displayed in browser tabs and bookmarks to represent
              your store
            </CustomTypography>
            <ImageUpload
              id="favicon"
              key="favicon"
              allowedFileTypes="image/*"
              fileInfo="SVG, PNG, JPG up to 1MB | Recommended size: 32x32 pixels"
              showImagesList={false}
              size="large"
              multiple={false}
              uploadText="Upload Favicon"
              previewImage={true}
              minWidth="100%"
              minHeight="134px"
              existingImageList={faviconImages.existing}
              newImageList={faviconImages.files}
              deleteImage={(imageData) => {
                removeBrandingImage(
                  "favicon",
                  (image) =>
                    typeof image === "string" && image === imageData.img
                );
              }}
              removeImage={(imageData) => {
                removeBrandingImage(
                  "favicon",
                  (image) =>
                    image instanceof File && image.name === imageData.title
                );
              }}
              addImagesToBeUploaded={(files) =>
                swagStoreStore.updateBranding({
                  favicon: files,
                })
              }
              disabled={false}
            />
          </Box>
        </Box>

        {/* <Divider sx={{ my: 3 }} /> */}

        <Box
          sx={{
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          <CustomTypography
            variant="body1"
            fontWeight={600}
            style={{ marginBottom: "12px" }}
          >
            Brand Colors
          </CustomTypography>

          <Box>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "12px", color: theme.palette.grey[800] }}
            >
              Primary Button Color
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.grey[600]}
              style={{ marginBottom: "12px", fontSize: "0.75rem" }}
            >
              Main colour for primary action buttons like "Add to Cart" and
              "Checkout" throughout your store
            </CustomTypography>

            <ColorPicker
              value={settings.primaryColor || "#1553CA"}
              onChange={(color) => onSettingChange("primaryColor", color)}
              label="Primary Color"
              mb={2}
            />
          </Box>

          <Box>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "12px", color: theme.palette.grey[800] }}
            >
              Secondary Button Color
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.grey[600]}
              style={{ marginBottom: "12px", fontSize: "0.75rem" }}
            >
              Colour that comes on hover of primary button
            </CustomTypography>

            <ColorPicker
              value={settings.secondaryColor || "#D7458F"}
              onChange={(color) => onSettingChange("secondaryColor", color)}
              label="Secondary Color"
              mb={0.5}
            />
          </Box>

          <Box>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{
                marginTop: "14px",
                marginBottom: "12px",
                color: theme.palette.grey[800],
              }}
            >
              Store Background Color
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.grey[600]}
              style={{ marginBottom: "12px", fontSize: "0.75rem" }}
            >
              Background colour for the entire store page, setting the overall
              visual theme
            </CustomTypography>

            <ColorPicker
              value={settings.storeBackgroundColor || "#FFFFFF"}
              onChange={(color) =>
                onSettingChange("storeBackgroundColor", color)
              }
              label="Store Background Color"
              mb={2}
            />
          </Box>

          <Box>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{
                marginTop: "14px",
                marginBottom: "12px",
                color: theme.palette.grey[800],
              }}
            >
              Homepage Product Card Background
            </CustomTypography>
            <CustomTypography
              variant="body3"
              color={theme.palette.grey[600]}
              style={{ marginBottom: "12px", fontSize: "0.75rem" }}
            >
              Background color for product cards on the homepage to make them
              stand out from the page background
            </CustomTypography>

            <ColorPicker
              value={settings.homepageProductCardBackground || "#FFFFFF"}
              onChange={(color) =>
                onSettingChange("homepageProductCardBackground", color)
              }
              label="Homepage Product Card Background"
              mb={2}
            />
          </Box>
        </Box>

        {/* Text Field Colors Section */}
        {/* <Box
          sx={{
            mt: 3,
            mb: 3,
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            p: 2.2,
            borderRadius: 2,
          }}
        >
          <CustomTypography
            variant="body1"
            fontWeight={600}
            style={{ marginBottom: "12px" }}
          >
            Text Field Colors
          </CustomTypography>

          <Box sx={{ mb: 2 }}>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "12px", color: theme.palette.grey[800] }}
            >
              Active color
            </CustomTypography>
            <ColorPicker
              value={settings.textFieldActiveColor || "#FFFFFF"}
              onChange={(color) =>
                onSettingChange("textFieldActiveColor", color)
              }
              label="Active color"
              mb={2}
            />
          </Box>

          <Box>
            <CustomTypography
              variant="body2"
              fontWeight={600}
              style={{ marginBottom: "12px", color: theme.palette.grey[800] }}
            >
              Disabled color
            </CustomTypography>
            <ColorPicker
              value={settings.textFieldDisabledColor || "#D7458F"}
              onChange={(color) =>
                onSettingChange("textFieldDisabledColor", color)
              }
              label="Disabled color"
              mb={0.5}
            />
          </Box>

          <CustomTypography
            variant="body3"
            fontWeight={500}
            style={{
              marginBottom: "16px",
              color: theme.palette.grey[800],
              marginTop: "16px",
            }}
          >
            PREVIEW
          </CustomTypography>

          <Box>
            <FormTextField
              placeholder="Your Active text field"
              disabled={false}
              sx={{
                marginBottom: "16px",
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: settings.textFieldActiveColor || "#6455ED",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: settings.textFieldActiveColor || "#6455ED",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: theme.palette.grey[500],
                  opacity: 1,
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 1 }}>
            <FormTextField
              placeholder="Your Disabled text field"
              disabled={true}
              sx={{
                marginBottom: "0px",
                "& .MuiOutlinedInput-root": {
                  backgroundColor:
                    settings.textFieldDisabledColor || "rgba(230, 230, 230, 1)",
                  "& fieldset": {
                    borderColor: theme.palette.customColors.greyBorder,
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: theme.palette.grey[500],
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box> */}

        {/* Other Settings Accordion */}
        <Accordion
          sx={{
            mt: 3,
            mb: 3,
            boxShadow: `${theme.palette.customColors.shadowVeryLight} 0px 6px 24px 0px, ${theme.palette.customColors.shadowLightBorder} 0px 0px 0px 1px`,
            borderRadius: 2,
            "&:before": {
              display: "none",
            },
            "&.Mui-expanded": {
              margin: "24px 0",
            },
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMore />}
            sx={{
              px: 2.2,
              py: 0,
              "& .MuiAccordionSummary-content": {
                my: 0,
              },
            }}
          >
            <CustomTypography variant="body1" fontWeight={600}>
              Other Settings
            </CustomTypography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.2, pb: 2.2 }}>
            {/* SEO Settings */}
            <Box sx={{ mb: 3 }}>
              <CustomTypography
                variant="body1"
                fontWeight={600}
                style={{ marginBottom: "16px", marginTop: "-15px" }}
              >
                SEO Settings
              </CustomTypography>

              <Box sx={{ mb: 2 }}>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  style={{
                    marginBottom: "12px",
                    color: theme.palette.grey[800],
                  }}
                >
                  Meta Title
                </CustomTypography>
                <CustomTypography
                  variant="body3"
                  color={theme.palette.grey[600]}
                  style={{ marginBottom: "12px", fontSize: "0.75rem" }}
                >
                  Title displayed in browser tabs and search engine results to
                  improve SEO and brand recognition
                </CustomTypography>
                <FormTextField
                  placeholder="Enter meta title"
                  value={settings.metaTitle || ""}
                  onChange={(id, value) =>
                    onSettingChange("metaTitle", value || "")
                  }
                  sx={{ marginBottom: "0px" }}
                  backgroundColor={
                    theme.palette.customColors.grayBackGroundColor
                  }
                  borderColor="none"
                />
              </Box>

              <Box>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  style={{
                    marginBottom: "12px",
                    color: theme.palette.grey[800],
                  }}
                >
                  Meta Description
                </CustomTypography>
                <CustomTypography
                  variant="body3"
                  color={theme.palette.grey[600]}
                  style={{ marginBottom: "12px", fontSize: "0.75rem" }}
                >
                  Brief description shown in search engine results to help users
                  understand what your store offers
                </CustomTypography>
                <FormTextField
                  placeholder="Enter meta description"
                  value={settings.metaDescription || ""}
                  onChange={(id, value) =>
                    onSettingChange("metaDescription", value || "")
                  }
                  multiline
                  rows={4}
                  sx={{ marginBottom: "0px" }}
                  backgroundColor={
                    theme.palette.customColors.grayBackGroundColor
                  }
                  borderColor="none"
                />
              </Box>
            </Box>

            {/* Footer Settings */}
            <Box>
              <CustomTypography
                variant="body1"
                fontWeight={600}
                style={{ marginBottom: "16px" }}
              >
                Footer Settings
              </CustomTypography>

              <Box>
                <CustomTypography
                  variant="body2"
                  fontWeight={600}
                  style={{
                    marginBottom: "12px",
                    color: theme.palette.grey[800],
                  }}
                >
                  Footer Text
                </CustomTypography>
                <CustomTypography
                  variant="body3"
                  color={theme.palette.grey[600]}
                  style={{ marginBottom: "12px", fontSize: "0.75rem" }}
                >
                  Text displayed at the bottom of every page, typically used for
                  copyright, contact info, or additional links
                </CustomTypography>
                <FormTextField
                  placeholder="Enter footer text"
                  value={settings.footerText || ""}
                  onChange={(id, value) =>
                    onSettingChange("footerText", value || "")
                  }
                  multiline
                  rows={4}
                  sx={{ marginBottom: "0px" }}
                  backgroundColor={
                    theme.palette.customColors.grayBackGroundColor
                  }
                  borderColor="none"
                />
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      </Box>

      <Box>
        <AppButton
          label="Save Changes"
          onClick={() => {
            onSaveSettings();
          }}
          disabled={!userStore.hasPermission(EmployeeRoles.EDIT_SWAGSTORE)}
          customStyles={{
            mt: 2,
            mb: 10,
            width: "100%",
            backgroundColor: theme.palette.customColors?.darkBlackBorder,
            color: theme.palette.customColors?.white,
            "&:hover": {
              backgroundColor: theme.palette.customColors?.darkBlackBorder,
              color: theme.palette.customColors?.white,
            },
            "&:disabled": {
              backgroundColor: theme.palette.customColors?.greyBorder,
              color: theme.palette.customColors?.white,
            },
          }}
          variant="primaryBlock"
        />
      </Box>
    </>
  );
};

export default BrandingSettings;
